import { ReactElement, useState, useEffect } from "react";

import { Box, CssBaseline, Grid, Link, Paper, Typography } from "@mui/material";

// import bg1 from "@/images/bg1.jpg";
import { ColorModeToggle } from "@/contexts/ColorModeContext";
import { useCommunities } from "@/hooks/useCommunities";
import abbypdImage from "@/images/abbypd-side-image.jpeg";
import apsImage from "@/images/aps-side-image.jpeg";
import bg2 from "@/images/bg2.jpg";
import bg3 from "@/images/bg3.jpg";
import brockvilleImage from "@/images/brockville-side-image.jpeg";
import ckpsImage from "@/images/ckps-side-image.jpeg";
import dcsoImage from "@/images/dcso-side-image.jpg";
import gspsImage from "@/images/gsps-side-image.jpg";
import hpsImage from "@/images/hps-side-image.jpg";
import lpsImage from "@/images/lps-side-image.jpg";
import nbpsImage from "@/images/nbps-side-image.jpg";
import nwpdImage from "@/images/nwpd-side-image.jpg";
import oppImage from "@/images/opp-side-image.png";
import ppsImage from "@/images/pps-side-image.jpg";
import prpImage from "@/images/prp-side-image.jpg";
import logo from "@/images/rubicon_portal_logo.png";
import vicpdImage from "@/images/vicpd-side-image.jpg";

import styles from "./Home.module.css";

function Copyright() {
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {"Copyright © "}
      <Link color="inherit" href="https://www.tryrubicon.com/">
        TryRubicon.com
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const LoginWrapper = ({ children }: { children: ReactElement }) => {
  const communities = useCommunities();
  const [finalImage, setFinalImage] = useState(bg2);
  const [finalVideo, setFinalVideo] = useState<string | null>(null);

  // DB-driven override: agency-specific login_background from the community
  // directory (image or video), keyed by the sso_slug in the path. Layered over
  // the hardcoded baseline below so it wins once the branding is in S3, and the
  // bundled fallback keeps working until then (and for orgs without one).
  useEffect(() => {
    if (communities.length === 0) return;
    const community = communities.find(
      (c) => c.sso_slug && location.pathname.includes(c.sso_slug),
    );
    const bgUrl = community?.branding?.login_background?.url;
    if (bgUrl && /^https?:\/\//i.test(bgUrl)) {
      // Inspect the fetched file instead of trusting a flag/extension: if the
      // browser can decode it as an image it's an image; otherwise it's a video.
      const probe = new Image();
      probe.onload = () => {
        setFinalImage(bgUrl);
        setFinalVideo(null);
      };
      probe.onerror = () => setFinalVideo(bgUrl);
      probe.src = bgUrl;
    }
  }, [communities]);

  useEffect(() => {
    const num = Math.floor(Math.random() * 10);
    let newImage = bg2;

    if ([1, 2, 3].includes(num)) newImage = bg2;
    if ([4, 5, 6].includes(num)) newImage = bg3;

    // Ideally refactor this into a service configuration value
    if (location.pathname.includes("ckps")) {
      newImage = ckpsImage;
    } else if (location.pathname.includes("anishinabek")) {
      newImage = apsImage;
    } else if (location.pathname.includes("abbotsford")) {
      newImage = abbypdImage;
    } else if (location.pathname.includes("brockville")) {
      newImage = brockvilleImage;
    } else if (location.pathname.includes("peterborough")) {
      newImage = ppsImage;
    } else if (location.pathname.includes("hamilton")) {
      newImage = hpsImage;
    } else if (location.pathname.includes("peel")) {
      newImage = prpImage;
    } else if (location.pathname.includes("gsps")) {
      newImage = gspsImage;
    } else if (location.pathname.includes("dcso")) {
      newImage = dcsoImage;
    } else if (location.pathname.includes("opp")) {
      newImage = oppImage;
    } else if (location.pathname.includes("london")) {
      newImage = lpsImage;
    } else if (location.pathname.includes("north-bay")) {
      newImage = nbpsImage;
    } else if (location.pathname.includes("new-westminster")) {
      newImage = nwpdImage;
    } else if (location.pathname.includes("victoria")) {
      newImage = vicpdImage;
    } else if (location.pathname.includes("ottawa")) {
      setFinalVideo("/service-videos/ottawa_police_service.mp4");
      return;
    }

    setFinalImage(newImage);
  }, []); // Empty dependency array means this runs once on mount

  return (
    <Grid container component="main" sx={{ height: "100vh" }}>
      <CssBaseline enableColorScheme />
      {/* These pages render outside MainLayout, so they never inherit the
          AppBar's colour-mode toggle. Mounting it here covers every page that
          uses LoginWrapper: login, service login, verify phone, add phone and
          set password. Fixed rather than absolute because the Grid container
          isn't a positioned ancestor. */}
      <Box sx={{ position: "fixed", top: 12, right: 12, zIndex: 1200 }}>
        <ColorModeToggle />
      </Box>
      <Grid
        size={{ xs: 12, sm: 4, md: 7 }}
        style={
          finalVideo
            ? { position: "relative", overflow: "hidden" }
            : {
                backgroundImage: "url(" + finalImage + ")",
                backgroundRepeat: "no-repeat",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }
        }
      >
        {finalVideo && (
          <video
            autoPlay
            muted
            loop
            playsInline
            src={finalVideo}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "left center",
            }}
          />
        )}
        <div
          className={styles.homeLogoContainer}
          style={finalVideo ? { position: "relative", zIndex: 1 } : undefined}
        >
          <img style={{ width: "150px" }} src={logo} alt="" />
        </div>
      </Grid>
      <Grid
        size={{ xs: 12, sm: 8, md: 5 }}
        component={Paper}
        elevation={6}
        square
      >
        {children}
        <Copyright />
      </Grid>
    </Grid>
  );
};

export default LoginWrapper;
